/** The By month tab of Scope 1 and Scope 2: the window's emissions month by month (ADR-0110).
 *
 * One row per calendar month, from `GET /inventory/monthly`, which reconciles exactly to the total
 * on the Figures tab. **The months are the server's** and so is the total under them; a month with
 * no records says *No records*, which is not a zero, and nothing here adds a column up. The one
 * filter is the site, and it is the server's too (the series is asked for again for that site, so a
 * site's months are never read out of the workspace's cache entry).
 */

import { Flame, Lightning } from "@phosphor-icons/react";
import { useState } from "react";

import { useFacilities } from "../../api/evidence";
import { useInventoryMonthly } from "../../api/queries";
import type { InventoryMonth } from "../../api/queries";
import { EngagementWindowNote } from "../../components/EngagementWindowNote";
import { ReadFailed } from "../../components/Loaded";
import type { FilterState } from "../../components/ui";
import {
  EMPTY_FILTERS,
  EmptyState,
  FilterLine,
  GridTable,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../../components/ui";
import type { GridColumn } from "../../components/ui";
import { monthLabel, tonnes } from "../../lib/format";
import { NO_RECEIPT, noReceipt } from "../../lib/noReceipt";
import { monthBreakdownKey } from "../bands/monthBreakdown";
import { OpenFigures } from "../tableKit";
import { DASH } from "./context";
import type { ScopeContext } from "./context";

interface Row {
  month: string;
  kg: string | undefined;
  recorded: boolean;
}

function rowOf(month: InventoryMonth, scope: string): Row {
  const entry = month.by_scope.find((candidate) => candidate.scope === scope);
  return { month: month.month, kg: entry?.assessed ? entry.kgco2e : undefined, recorded: Boolean(entry?.assessed) };
}

export function MonthView({ ctx }: { ctx: ScopeContext }) {
  const { scope, period, unread } = ctx;
  const facilities = useFacilities();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const siteId = filters.values["site"]?.[0];
  const read = useInventoryMonthly(period, "location", siteId);

  const rows = (read.data?.months ?? []).map((month) => rowOf(month, scope));
  const recorded = rows.filter((row) => row.recorded);
  const largest = recorded.reduce<Row | undefined>(
    (best, row) => (best === undefined || Number(row.kg) > Number(best.kg) ? row : best),
    undefined,
  );
  const serverTotal = read.data?.totals_by_scope.find((card) => card.scope === scope);
  const dash = read.isPending || Boolean(read.error);

  const columns: GridColumn<Row>[] = [
    { id: "month", header: "Month", cell: (row) => monthLabel(row.month) },
    {
      id: "tonnes",
      header: "tCO₂e",
      numeric: true,
      cell: (row) =>
        row.kg === undefined ? (
          <span>—</span>
        ) : siteId ? (
          // A site's month is the site's share of it; the month's figures the drawer lists are every
          // site's, so a narrowed figure says what it is instead of opening a different set (rule 3).
          <span {...noReceipt(NO_RECEIPT.siteMonth)}>{tonnes(row.kg)}</span>
        ) : (
          <OpenFigures
            screen={ctx.screen}
            nodeKey={monthBreakdownKey(row.month)}
            value={tonnes(row.kg)}
            name={`Scope ${scope} ${monthLabel(row.month)}`}
          />
        ),
    },
    {
      id: "state",
      header: "Records",
      cell: (row) =>
        row.recorded ? (
          <StatusChip tone="ok" size="sm">
            Recorded
          </StatusChip>
        ) : (
          <StatusChip tone="neutral" size="sm">
            No records
          </StatusChip>
        ),
    },
  ];

  const failure =
    unread && ctx.failed ? (
      <ReadFailed of={`The Scope ${scope} figures`} error={ctx.failed} onRetry={ctx.retry} />
    ) : read.error ? (
      <ReadFailed of="The monthly series" error={read.error} onRetry={() => void read.refetch()} />
    ) : facilities.error ? (
      <ReadFailed
        of="The list of sites"
        error={facilities.error}
        onRetry={() => void facilities.refetch()}
      />
    ) : undefined;

  return (
    <TablePage
      icon={scope === "1" ? Flame : Lightning}
      title={`Scope ${scope}`}
      extra={<EngagementWindowNote under={ctx.engagement} />}
      tiles={
        <TileStrip label={`Scope ${scope} month totals`}>
          <Tile label="Months recorded" value={dash ? DASH : `${String(recorded.length)} of ${String(rows.length)}`} loading={read.isPending} />
          <Tile label="Months empty" value={dash ? DASH : String(rows.length - recorded.length)} loading={read.isPending} />
          <Tile label="Largest month" value={dash || !largest ? DASH : monthLabel(largest.month)} loading={read.isPending} />
        </TileStrip>
      }
      filters={
        failure ? undefined : (
          <FilterLine
            filters={[
              {
                id: "site",
                label: "Site",
                mode: "single",
                allLabel: "All sites",
                options: (facilities.data ?? []).map((site) => ({ value: site.id, label: site.name })),
              },
            ]}
            state={filters}
            onChange={setFilters}
            searchLabel="Search this table"
          />
        )
      }
      table={
        failure ?? (
          <GridTable
            caption={`Scope ${scope} by month`}
            columns={columns}
            rows={rows}
            rowKey={(row) => row.month}
            rowLabel={(row) => monthLabel(row.month)}
            loading={read.isPending}
            totals={
              serverTotal?.assessed
                ? { label: "Total", values: { tonnes: tonnes(serverTotal.kgco2e) } }
                : undefined
            }
            empty={<EmptyState title="No months in this period." compact />}
            testId="scope-months"
          />
        )
      }
    />
  );
}
